// Workflow blueprints: designs I would ship into a production environment,
// none of them running today. They live apart from `automations` on purpose,
// and the page labels every one of them as a design. If one of these gets
// built for a client, it does not move into `automations` unless it is my own
// system that I can point at.
//
// Each blueprint carries more than a picture, because the picture is the easy
// part. `controls` is what makes it survivable in a real company: idempotency,
// audit, a dead letter path, a rollback, and a named human at the point where a
// mistake costs money. `failure` says what happens on the bad day.

export const blueprints = [
  {
    slug: 'support-triage',
    name: 'Support triage and reply drafting',
    subtitle: 'Shared inbox or Zendesk, 500 to 5,000 tickets a week',
    summary:
      'Classifies every incoming ticket, answers the ones it can prove from the knowledge base, and hands the rest to a person with the context already gathered. Agents keep the send button.',
    replaces: 'A queue triaged by hand every morning, and first replies measured in hours',
    runsOn: ['Zendesk or Front', 'Claude', 'Vector store over your docs', 'Temporal or a queue', 'Postgres'],
    controls: [
      'Retrieval only over approved docs; a reply with no citation is never sent',
      'Confidence and intent gates, tuned per queue rather than globally',
      'PII redacted before the model sees the ticket, restored in the draft',
      'Every draft, citation and decision written to an audit table',
      'Reply templates versioned; a rollback swaps the version, not the code',
    ],
    failure:
      'Model or retrieval down means every ticket routes to a human queue with a banner. Nothing auto sends while the citation check is failing.',
    nodes: [
      { id: 'in', label: 'Ticket arrives', note: 'webhook', kind: 'trigger', col: 0, row: 1 },
      { id: 'redact', label: 'Redact PII', note: 'before any model call', kind: 'step', col: 1, row: 1 },
      { id: 'class', label: 'Classify', note: 'intent, urgency, product', kind: 'model', col: 2, row: 1 },
      { id: 'route', label: 'Auto answerable?', kind: 'gate', col: 3, row: 1 },
      { id: 'retr', label: 'Retrieve policy', note: 'approved docs only', kind: 'step', col: 4, row: 0 },
      { id: 'draft', label: 'Draft reply', note: 'with citations', kind: 'model', col: 5, row: 0 },
      { id: 'check', label: 'Citations hold?', kind: 'gate', col: 6, row: 0 },
      { id: 'send', label: 'Send', note: 'per queue policy', kind: 'store', col: 7, row: 0 },
      { id: 'human', label: 'Agent queue', note: 'context attached', kind: 'end', col: 5, row: 2 },
      { id: 'audit', label: 'Audit log', note: 'decision and sources', kind: 'store', col: 7, row: 2 },
    ],
    edges: [
      ['in', 'redact'], ['redact', 'class'], ['class', 'route'],
      ['route', 'retr', 'yes'], ['route', 'human', 'no'],
      ['retr', 'draft'], ['draft', 'check'],
      ['check', 'send', 'pass'], ['check', 'human', 'fail'],
      ['send', 'audit'], ['human', 'audit'],
    ],
  },
  {
    slug: 'invoice-intake',
    name: 'Invoice intake to ERP posting',
    subtitle: 'Accounts payable, 2,000 to 20,000 invoices a month',
    summary:
      'Reads the invoice, matches it against the purchase order and the receipt, posts the clean ones, and sends only genuine exceptions to a human with the mismatch highlighted.',
    replaces: 'Keying invoices by hand and chasing three way match discrepancies over email',
    runsOn: ['Email or supplier portal', 'Document extraction', 'NetSuite or SAP', 'Queue with retries', 'Postgres'],
    controls: [
      'Idempotency on supplier plus invoice number, so a resend cannot double pay',
      'Totals recomputed in code; extracted numbers are inputs, never the answer',
      'Tolerance thresholds per supplier, approved by finance and versioned',
      'Anything over the approval limit routes to a named approver, always',
      'Dead letter queue with the original document attached, nothing silently dropped',
    ],
    failure:
      'Extraction confidence below the floor, or any mismatch outside tolerance, goes to the exception queue. The pipeline stops posting before it guesses at money.',
    nodes: [
      { id: 'in', label: 'Invoice arrives', note: 'email or portal', kind: 'trigger', col: 0, row: 1 },
      { id: 'dedupe', label: 'Seen before?', note: 'idempotency key', kind: 'gate', col: 1, row: 1 },
      { id: 'skip', label: 'Ignore', note: 'already processed', kind: 'end', col: 2, row: 2 },
      { id: 'extract', label: 'Extract fields', note: 'line items, tax, totals', kind: 'model', col: 2, row: 0 },
      { id: 'recompute', label: 'Recompute totals', note: 'in code', kind: 'step', col: 3, row: 0 },
      { id: 'match', label: 'Three way match', note: 'PO, receipt, invoice', kind: 'step', col: 4, row: 0 },
      { id: 'gate', label: 'Within tolerance?', kind: 'gate', col: 5, row: 0 },
      { id: 'limit', label: 'Under approval limit?', kind: 'gate', col: 6, row: 0 },
      { id: 'post', label: 'Post to ERP', kind: 'store', col: 7, row: 0 },
      { id: 'exc', label: 'Exception queue', note: 'mismatch highlighted', kind: 'end', col: 6, row: 2 },
      { id: 'approve', label: 'Approver', note: 'named, per amount', kind: 'gate', col: 7, row: 2 },
    ],
    edges: [
      ['in', 'dedupe'], ['dedupe', 'skip', 'yes'], ['dedupe', 'extract', 'no'],
      ['extract', 'recompute'], ['recompute', 'match'], ['match', 'gate'],
      ['gate', 'limit', 'yes'], ['gate', 'exc', 'no'],
      ['limit', 'post', 'yes'], ['limit', 'approve', 'no'], ['approve', 'post'],
    ],
  },
  {
    slug: 'contract-review',
    name: 'Contract review against your playbook',
    subtitle: 'Legal and deal desk, first pass in minutes rather than days',
    summary:
      'Splits an incoming contract into clauses, compares each one to your own playbook, and produces a redline with the deviation, the fallback position, and who has to approve it. Counsel decides, always.',
    replaces: 'A lawyer reading every first draft to find the same six problems',
    runsOn: ['SharePoint or Drive', 'Claude', 'Clause library in Postgres', 'DocuSign', 'Slack for approvals'],
    controls: [
      'Clause library is the source of truth; the model matches against it and cites the clause id',
      'Risk tiers map to named approvers, so nothing depends on who is online',
      'No autonomous signature, ever. The pipeline ends at a prepared redline',
      'Every run stores the document hash, model, prompt version and clause ids used',
      'Unmatched clause types are escalated, not guessed at',
    ],
    failure:
      'An unrecognized clause type or a low match score routes the whole document to counsel unreviewed rather than partially annotated, so nobody trusts a half finished markup.',
    nodes: [
      { id: 'in', label: 'Contract in', note: 'upload or email', kind: 'trigger', col: 0, row: 1 },
      { id: 'split', label: 'Split clauses', note: 'typed sections', kind: 'step', col: 1, row: 1 },
      { id: 'match', label: 'Match to playbook', note: 'cites clause id', kind: 'model', col: 2, row: 1 },
      { id: 'known', label: 'Type recognized?', kind: 'gate', col: 3, row: 1 },
      { id: 'counsel', label: 'Counsel review', note: 'unreviewed document', kind: 'end', col: 4, row: 2 },
      { id: 'risk', label: 'Score deviation', note: 'tier 1 to 3', kind: 'model', col: 4, row: 0 },
      { id: 'redline', label: 'Draft redline', note: 'with fallback language', kind: 'model', col: 5, row: 0 },
      { id: 'approver', label: 'Route to approver', note: 'by risk tier', kind: 'gate', col: 6, row: 0 },
      { id: 'pack', label: 'Signature package', note: 'prepared, not sent', kind: 'store', col: 7, row: 0 },
      { id: 'audit', label: 'Audit record', kind: 'store', col: 7, row: 2 },
    ],
    edges: [
      ['in', 'split'], ['split', 'match'], ['match', 'known'],
      ['known', 'risk', 'yes'], ['known', 'counsel', 'no'],
      ['risk', 'redline'], ['redline', 'approver'], ['approver', 'pack', 'approved'],
      ['pack', 'audit'], ['counsel', 'audit'],
    ],
  },
  {
    slug: 'joiner-mover-leaver',
    name: 'Joiner, mover, leaver provisioning',
    subtitle: 'IT and security, audit ready access changes',
    summary:
      'Turns an HR record change into the exact set of access grants and removals, with an approval where policy requires one and a revocation path that runs the same day someone leaves.',
    replaces: 'Onboarding tickets copied from the last hire, and leavers who keep access for weeks',
    runsOn: ['Workday or BambooHR', 'Okta or Entra ID', 'Google Workspace', 'Jira', 'Temporal'],
    controls: [
      'Role based entitlement matrix in version control; the pipeline never invents a grant',
      'Every step idempotent, so a replay converges instead of duplicating accounts',
      'Separation of duties: the requester cannot approve their own elevation',
      'Leaver path runs on its own schedule and alerts if a revocation fails twice',
      'Quarterly access review export straight from the same table auditors ask for',
    ],
    failure:
      'A failed grant leaves the account with less access and a ticket, never more. A failed revocation pages a human, because that is the one that matters.',
    nodes: [
      { id: 'hr', label: 'HR event', note: 'joiner, mover, leaver', kind: 'trigger', col: 0, row: 1 },
      { id: 'resolve', label: 'Resolve entitlements', note: 'role matrix in git', kind: 'step', col: 1, row: 1 },
      { id: 'diff', label: 'Compute the diff', note: 'grants and removals', kind: 'step', col: 2, row: 1 },
      { id: 'sensitive', label: 'Privileged access?', kind: 'gate', col: 3, row: 1 },
      { id: 'approve', label: 'Manager and security', note: 'separation of duties', kind: 'gate', col: 4, row: 0 },
      { id: 'apply', label: 'Apply changes', note: 'idempotent per system', kind: 'step', col: 5, row: 1 },
      { id: 'verify', label: 'Verify state', note: 'read back from each system', kind: 'gate', col: 6, row: 1 },
      { id: 'page', label: 'Page on failure', note: 'revocations first', kind: 'end', col: 7, row: 2 },
      { id: 'record', label: 'Access record', note: 'audit export', kind: 'store', col: 7, row: 0 },
    ],
    edges: [
      ['hr', 'resolve'], ['resolve', 'diff'], ['diff', 'sensitive'],
      ['sensitive', 'approve', 'yes'], ['sensitive', 'apply', 'no'], ['approve', 'apply'],
      ['apply', 'verify'], ['verify', 'record', 'clean'], ['verify', 'page', 'drift'],
    ],
  },
  {
    slug: 'renewal-watch',
    name: 'Renewal and churn watch',
    subtitle: 'Customer success, every account scored weekly',
    summary:
      'Pulls product usage, support history and CRM activity into one score per account, writes the reason in plain language, and creates the task for the owner. Numbers come from the warehouse, not from the model.',
    replaces: 'A spreadsheet updated the week before the renewal call',
    runsOn: ['Snowflake or BigQuery', 'Salesforce or HubSpot', 'Zendesk', 'dbt', 'Claude for the narrative only'],
    controls: [
      'Every figure computed in the warehouse and versioned as a dbt model',
      'The model explains the score and may not change it, the same rule ROAM uses',
      'Score plus inputs snapshotted per run, so last quarter can be replayed',
      'Thresholds owned by the CS lead, changed by pull request',
      'No customer facing message sent automatically; the output is an owner task',
    ],
    failure:
      'A stale warehouse table drops that input and lowers confidence rather than scoring the account on partial data. A score with low confidence is shown as low confidence.',
    nodes: [
      { id: 'cron', label: 'Weekly', kind: 'trigger', col: 0, row: 1 },
      { id: 'usage', label: 'Product usage', note: 'warehouse', kind: 'source', col: 1, row: 0 },
      { id: 'support', label: 'Support history', kind: 'source', col: 1, row: 1 },
      { id: 'crm', label: 'CRM activity', kind: 'source', col: 1, row: 2 },
      { id: 'score', label: 'Risk score', note: 'dbt, versioned', kind: 'step', col: 2, row: 1 },
      { id: 'conf', label: 'Inputs fresh?', kind: 'gate', col: 3, row: 1 },
      { id: 'why', label: 'Explain the score', note: 'narrative only', kind: 'model', col: 4, row: 0 },
      { id: 'low', label: 'Flag low confidence', kind: 'end', col: 4, row: 2 },
      { id: 'task', label: 'Owner task', note: 'in the CRM', kind: 'store', col: 5, row: 0 },
      { id: 'brief', label: 'Renewal brief', note: 'for the call', kind: 'store', col: 6, row: 0 },
    ],
    edges: [
      ['cron', 'usage'], ['cron', 'support'], ['cron', 'crm'],
      ['usage', 'score'], ['support', 'score'], ['crm', 'score'],
      ['score', 'conf'], ['conf', 'why', 'yes'], ['conf', 'low', 'no'],
      ['why', 'task'], ['task', 'brief'],
    ],
  },
  {
    slug: 'agent-release-gate',
    name: 'Agent evaluation and release gate',
    subtitle: 'The workflow that lets you change the other workflows',
    summary:
      'Every prompt, model or tool change runs against a graded set before it reaches customers, ships to a small share of traffic first, and rolls back on its own when quality or cost moves the wrong way.',
    replaces: 'Shipping a prompt edit straight to production and waiting for complaints',
    runsOn: ['GitHub Actions', 'Eval harness', 'Feature flags', 'OpenTelemetry', 'Postgres or ClickHouse'],
    controls: [
      'Golden set with cases that have burned you, growing from real incidents',
      'Pass thresholds and cost per task budget both block the merge',
      'Canary on a small traffic share with an automatic rollback trigger',
      'Prompt and model pinned per release, so a regression is bisectable',
      'Traces sampled with p50, p95 and p99 latency and cost per request',
    ],
    failure:
      'A regression on the golden set, a cost per task over budget, or a canary error rate above the trigger rolls the release back to the last pinned version without waiting for a person.',
    nodes: [
      { id: 'pr', label: 'Change proposed', note: 'prompt, model, tool', kind: 'trigger', col: 0, row: 1 },
      { id: 'evals', label: 'Run the graded set', kind: 'step', col: 1, row: 1 },
      { id: 'quality', label: 'Quality threshold?', kind: 'gate', col: 2, row: 1 },
      { id: 'cost', label: 'Cost per task?', kind: 'gate', col: 3, row: 0 },
      { id: 'block', label: 'Block the merge', note: 'with the failing cases', kind: 'end', col: 3, row: 2 },
      { id: 'canary', label: 'Canary release', note: 'small traffic share', kind: 'step', col: 4, row: 0 },
      { id: 'watch', label: 'Watch traces', note: 'errors, latency, cost', kind: 'step', col: 5, row: 0 },
      { id: 'decide', label: 'Holding up?', kind: 'gate', col: 6, row: 0 },
      { id: 'roll', label: 'Roll forward', note: 'pin the version', kind: 'store', col: 7, row: 0 },
      { id: 'back', label: 'Roll back', note: 'automatic', kind: 'end', col: 7, row: 2 },
    ],
    edges: [
      ['pr', 'evals'], ['evals', 'quality'],
      ['quality', 'cost', 'pass'], ['quality', 'block', 'fail'],
      ['cost', 'canary', 'under'], ['cost', 'block', 'over'],
      ['canary', 'watch'], ['watch', 'decide'],
      ['decide', 'roll', 'yes'], ['decide', 'back', 'no'],
    ],
  },
];
